'use client'

import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'

export interface NumberInputProps {
    label?: string
    icon?: React.ReactNode
    value?: number
    onChange?: (value: number) => void
    min?: number
    max?: number
    step?: number
    placeholder?: string
    isRequired?: boolean
    className?: string
}

import { UIConstants } from "@/constants/ui";

export default function NumberInput({
    label,
    icon,
    value = 0,
    onChange,
    min = UIConstants.NUMBER_INPUT_DEFAULT_MIN,
    max = UIConstants.NUMBER_INPUT_DEFAULT_MAX,
    step = UIConstants.NUMBER_INPUT_DEFAULT_STEP,
    placeholder,
    isRequired = false,
    className = '',
}: NumberInputProps) {
    const [internalValue, setInternalValue] = useState(value)
    
    const currentValue = onChange ? value : internalValue
    
    const handleChange = useCallback((newValue: number) => {
        // Min/max kontrolü
        const clampedValue = Math.max(min, Math.min(max, newValue))
        
        if (onChange) {
            onChange(clampedValue)
        } else {
            setInternalValue(clampedValue)
        }
    }, [min, max, onChange])
    
    const handleIncrement = useCallback(() => {
        handleChange(currentValue + step)
    }, [currentValue, step, handleChange])
    
    const handleDecrement = useCallback(() => {
        handleChange(currentValue - step)
    }, [currentValue, step, handleChange])
    
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        if (inputValue === '') {
            handleChange(0)
            return
        }
        const numValue = parseInt(inputValue, 10)
        if (!isNaN(numValue)) {
            handleChange(numValue)
        }
    }, [handleChange])
    
    return (
        <motion.div
            className={`w-full ${className}`}
            variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
        >
            {label && (
                <label className="text-alsero-dark-gray text-lg mb-1 block">
                    {label}
                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            <div className="relative flex flex-row justify-between items-center px-6 py-3 bg-white rounded-lg w-full min-h-[60px]">
                <div className="flex flex-row gap-2 items-center flex-1">
                    {icon && <div className="flex-shrink-0">{icon}</div>}
                    <input
                        type="number"
                        value={currentValue}
                        onChange={handleInputChange}
                        placeholder={placeholder}
                        min={min}
                        max={max}
                        step={step}
                        className="w-full text-alsero-dark-gray text-lg bg-transparent border-none outline-none placeholder:text-alsero-light-gray placeholder:opacity-60"
                        required={isRequired}
                    />
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0 ml-2">
                    <button
                        type="button"
                        onClick={handleIncrement}
                        disabled={currentValue >= max}
                        className="text-alsero-dark-gray hover:text-alsero-first disabled:text-alsero-light-gray disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Increment"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 10L8 6L4 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={handleDecrement}
                        disabled={currentValue <= min}
                        className="text-alsero-dark-gray hover:text-alsero-first disabled:text-alsero-light-gray disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Decrement"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </motion.div>
    )
}

